import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    DestroyRef,
    ElementRef,
    EventEmitter,
    forwardRef,
    inject,
    InjectionToken,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { QwykTableTemplateDirective } from './table-template.directive';

// Forward-declared token so QwykSortableColumnDirective / QwykSortIconComponent
// can inject the host table without a circular import.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const QWYK_TABLE = new InjectionToken<any>('QwykTableComponent');

export interface QwykSortEvent {
    field: string;
    order: 1 | -1;
}

export interface QwykColumnReorderEvent {
    columns: unknown[];
    dragIndex: number;
    dropIndex: number;
}

export interface QwykFilter {
    closeOverlay(): void;
}

export interface QwykFilterRule {
    value: unknown;
    matchMode: string;
}

@Component({
    standalone: true,
    imports: [NgIf, NgForOf, NgTemplateOutlet, QwykTableTemplateDirective],
    selector: 'qwyk-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{ provide: QWYK_TABLE, useExisting: forwardRef(() => QwykTableComponent) }],
})
export class QwykTableComponent<T = unknown> implements AfterContentInit, AfterViewInit, OnInit, OnChanges {
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _destroyRef = inject(DestroyRef);

    @ViewChild('scrollEl') private _scrollElRef: ElementRef<HTMLElement> | null = null;

    @Input() value: T[] = [];
    @Input() loading = false;
    @Input() columns: unknown[] = [];
    @Input() scrollHeight: string | null = null;
    /** When true: skip internal sort, always emit sortChange (including sort reset with order=0) */
    @Input() externalSort = false;
    /**
     * When true: skip internal filtering — the parent applies the column
     * filters to the full dataset (before pagination) and feeds this table the
     * already-filtered page slice. `filterChange` is still emitted so the parent
     * can react. Use `applyColumnFilters()` to reproduce this table's matching.
     */
    @Input() externalFilter = false;
    /** External sort field — syncs internal state when provided by parent */
    @Input() sortField: string | null = null;
    /** External sort order — 1 = asc, -1 = desc, 0 = none */
    @Input() sortOrder: 1 | -1 | 0 = 0;
    /** Key for persisting column widths in sessionStorage / localStorage */
    @Input() stateKey: string | null = null;
    /** Storage backend for state persistence */
    @Input() stateStorage: 'session' | 'local' = 'session';

    @Output() sortChange = new EventEmitter<QwykSortEvent | { field: null; order: 0 }>();
    @Output() columnReorder = new EventEmitter<QwykColumnReorderEvent>();
    @Output() filterChange = new EventEmitter<Record<string, QwykFilterRule>>();

    @ContentChildren(QwykTableTemplateDirective)
    templates: QueryList<QwykTableTemplateDirective>;

    captionTemplate: TemplateRef<unknown> | null = null;
    headerTemplate: TemplateRef<unknown> | null = null;
    bodyTemplate: TemplateRef<unknown> | null = null;
    summaryTemplate: TemplateRef<unknown> | null = null;
    loadingBodyTemplate: TemplateRef<unknown> | null = null;
    emptyMessageTemplate: TemplateRef<unknown> | null = null;

    /** Internal sort state — driven by sort() or synced from @Input sortField/sortOrder */
    _sortField: string | null = null;
    _sortOrder: 1 | -1 | 0 = 0;

    /** Sorted copy of value rendered by the template */
    _displayedValue: T[] = [];

    /** Column order copy — updated by reorder drag-drop */
    _displayedColumns: unknown[] = [];

    /** Notifies injected consumers (sort icon, directive) when sort state changes */
    readonly _sortState$ = new Subject<void>();

    /** Column widths persisted by QwykResizableColumnDirective */
    _columnWidths: Record<string, number> = {};

    /** Persisted expanded table width after column resize */
    _tableWidth: number | null = null;

    /** Set by QwykColumnFilterComponent — blocks column drag while a filter overlay is open */
    _filterOverlayOpen = false;

    /** Active filter overlay — set by QwykColumnFilterComponent */
    _activeFilter: QwykFilter | null = null;

    /** Active column filters — set by QwykColumnFilterComponent */
    _filters: Record<string, QwykFilterRule> = {};

    /** Index of the column being dragged — set by QwykReorderableColumnDirective */
    _dragColumnIndex: number | null = null;

    /** Scroll shadow visibility flags */
    _shadowLeft = false;
    _shadowRight = false;
    _shadowTop = false;
    _shadowBottom = false;

    /** Height of the sticky thead — used to offset the top shadow below the header row */
    _theadHeight = 0;

    private _resizeObserver: ResizeObserver | null = null;

    ngOnInit(): void {
        this._loadState();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['sortField'] !== undefined) {
            this._sortField = this.sortField;
        }
        if (changes['sortOrder'] !== undefined) {
            this._sortOrder = this.sortOrder;
        }
        if (changes['value']) {
            this._applyView();
            if (!changes['value'].firstChange) {
                this._scrollToTop();
            }
        }
        if (changes['columns']) {
            this._displayedColumns = [...(this.columns || [])];
        }
        if (changes['stateKey'] && !changes['stateKey'].firstChange) {
            this._loadState();
        }
    }

    ngAfterViewInit(): void {
        this._updateShadows();
        if (this._scrollElRef?.nativeElement && typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => {
                this._updateShadows();
                this._cdr.markForCheck();
            });
            this._resizeObserver.observe(this._scrollElRef.nativeElement);
            // Also observe the inner table to catch column resize / content changes
            const table = this._scrollElRef.nativeElement.querySelector('table');
            if (table) this._resizeObserver.observe(table);
        }
        this._destroyRef.onDestroy(() => this._resizeObserver?.disconnect());
    }

    _onScroll(): void {
        this._updateShadows();
        this._cdr.markForCheck();
    }

    _updateShadows(): void {
        const el = this._scrollElRef?.nativeElement;
        if (!el) return;
        const THRESHOLD = 1; // px — avoids floating-point edge flicker
        this._shadowLeft   = el.scrollLeft > THRESHOLD;
        this._shadowRight  = el.scrollLeft + el.clientWidth < el.scrollWidth - THRESHOLD;
        this._shadowTop    = el.scrollTop > THRESHOLD;
        this._shadowBottom = el.scrollTop + el.clientHeight < el.scrollHeight - THRESHOLD;
        this._theadHeight  = (el.querySelector('thead') as HTMLElement | null)?.offsetHeight ?? 0;
    }

    ngAfterContentInit(): void {
        this.resolveTemplates();
        this.templates.changes.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this.resolveTemplates());
    }

    /** Called by QwykSortableColumnDirective on header click */
    sort(field: string): void {
        if (this._sortField === field) {
            if (this._sortOrder === 1) {
                this._sortOrder = -1;
            } else if (this._sortOrder === -1) {
                this._sortField = null;
                this._sortOrder = 0;
            } else {
                this._sortOrder = 1;
            }
        } else {
            this._sortField = field;
            this._sortOrder = 1;
        }
        if (!this.externalSort) {
            this._applyView();
            this._persistState();
        }
        this._sortState$.next();
        this._cdr.markForCheck();
        this.sortChange.emit(
            this._sortField !== null
                ? { field: this._sortField, order: this._sortOrder as 1 | -1 }
                : { field: null, order: 0 }
        );
    }

    /** Called by QwykColumnFilterComponent to update a single column filter */
    setFilter(field: string, value: unknown, matchMode: string): void {
        const isEmpty =
            value === null ||
            value === undefined ||
            value === '' ||
            (Array.isArray(value) && value.length === 0);
        if (isEmpty) {
            delete this._filters[field];
        } else {
            this._filters[field] = { value, matchMode };
        }
        this._applyView();
        this._cdr.markForCheck();
        this.filterChange.emit({ ...this._filters });
    }

    /** Called by QwykColumnFilterComponent to clear a single column filter */
    clearFilter(field: string): void {
        delete this._filters[field];
        this._applyView();
        this._cdr.markForCheck();
        this.filterChange.emit({ ...this._filters });
    }

    registerFilter(filter: QwykFilter): void {
        if (this._activeFilter && this._activeFilter !== filter) {
            this._activeFilter.closeOverlay();
        }
        this._activeFilter = filter;
    }

    unregisterFilter(filter: QwykFilter): void {
        if (this._activeFilter === filter) {
            this._activeFilter = null;
        }
    }

    /** Clears all active filters (e.g. from a "Clear all" button) */
    clearAllFilters(): void {
        this._filters = {};
        this._applyView();
        this._cdr.markForCheck();
        this.filterChange.emit({});
    }

    /** Called by QwykResizableColumnDirective after user finishes resizing */
    _saveColumnWidth(columnId: string, width: number): void {
        this._columnWidths[columnId] = width;
        this._persistState();
    }

    /** Called by QwykResizableColumnDirective on AfterViewInit to restore saved width */
    _getColumnWidth(columnId: string): number | null {
        return this._columnWidths[columnId] ?? null;
    }

    /** Called by QwykResizableColumnDirective with final table width after resize ends */
    _saveTableWidth(width: number): void {
        this._tableWidth = width;
        this._persistState();
    }

    /** Called by QwykResizableColumnDirective on mouseup — saves ALL column widths at once */
    _batchSaveColumnWidths(widths: Record<string, number>, tableWidth: number | null): void {
        this._columnWidths = { ...this._columnWidths, ...widths };
        if (tableWidth != null) this._tableWidth = tableWidth;
        this._persistState();
        this._cdr.markForCheck(); // _tableWidth change activates .qwyk-table-fixed
    }

    /** Called by QwykReorderableColumnDirective when a column is dropped */
    _onColumnDrop(dropIndex: number): void {
        const dragIndex = this._dragColumnIndex;
        if (dragIndex == null || dragIndex === dropIndex) {
            this._dragColumnIndex = null;
            return;
        }
        const cols = [...this._displayedColumns];
        const [moved] = cols.splice(dragIndex, 1);
        cols.splice(dropIndex, 0, moved);
        this._displayedColumns = cols;
        this._dragColumnIndex = null;
        this._persistState(); // persist new column order
        this._cdr.markForCheck();
        this.columnReorder.emit({ columns: cols, dragIndex, dropIndex });
    }

    private _loadState(): void {
        if (!this.stateKey) return;
        try {
            const storage = this.stateStorage === 'local' ? localStorage : sessionStorage;
            const raw = storage.getItem(this.stateKey);
            if (!raw) return;
            const state = JSON.parse(raw) as {
                sortField?: string;
                sortOrder?: 1 | -1 | 0;
                columnWidths?: Record<string, number>;
                columnOrder?: string[];
                tableWidth?: number;
            };
            if (state.columnWidths) {
                this._columnWidths = state.columnWidths;
            }
            if (state.tableWidth) {
                this._tableWidth = state.tableWidth;
            }
            // Restore column order
            if (Array.isArray(state.columnOrder) && state.columnOrder.length > 0 && this._displayedColumns.length > 0) {
                const orderMap = new Map<string, number>(
                    state.columnOrder.map((id, i) => [id, i] as [string, number])
                );
                this._displayedColumns = [...this._displayedColumns].sort((a, b) => {
                    const aId = (a as { id?: string })?.id;
                    const bId = (b as { id?: string })?.id;
                    const len = this._displayedColumns.length;
                    const aIdx = aId != null ? (orderMap.get(aId) ?? len) : len;
                    const bIdx = bId != null ? (orderMap.get(bId) ?? len) : len;
                    return aIdx - bIdx;
                });
                this._cdr.markForCheck();
            }
            if (!this.externalSort && state.sortField) {
                this._sortField = state.sortField;
                this._sortOrder = state.sortOrder ?? 1;
                this._applyView();
                this._sortState$.next();
            }
        } catch {
            // ignore corrupt storage entries
        }
    }

    private _persistState(): void {
        if (!this.stateKey) return;
        try {
            const storage = this.stateStorage === 'local' ? localStorage : sessionStorage;
            const state: Record<string, unknown> = {
                columnWidths: this._columnWidths,
                columnOrder: (this._displayedColumns as { id?: string }[])
                    .map(c => c?.id)
                    .filter((id): id is string => !!id),
                tableWidth: this._tableWidth,
            };
            if (!this.externalSort) {
                state['sortField'] = this._sortField;
                state['sortOrder'] = this._sortOrder;
            }
            storage.setItem(this.stateKey, JSON.stringify(state));
        } catch {
            // ignore quota errors
        }
    }

    private _scrollToTop(): void {
        const el = this._scrollElRef?.nativeElement;
        if (el) el.scrollTop = 0;
    }

    private _applyView(): void {
        let data: T[] = this.value ? [...this.value] : [];
        // 1. Filter (client-side, unless the parent filters the full dataset itself)
        if (!this.externalFilter) {
            data = applyColumnFilters(data, this._filters);
        }
        // 2. Sort (client-side only when externalSort is off)
        if (!this.externalSort && this._sortField && this._sortOrder !== 0) {
            const field = this._sortField;
            const order = this._sortOrder;
            data.sort((a, b) => {
                const va = _deepGet(a, field);
                const vb = _deepGet(b, field);
                let result: number;
                if (va == null && vb == null) result = 0;
                else if (va == null) result = -1;
                else if (vb == null) result = 1;
                else if (typeof va === 'string' && typeof vb === 'string') result = va.localeCompare(vb);
                else result = va < vb ? -1 : va > vb ? 1 : 0;
                return order * result;
            });
        }
        this._displayedValue = data;
        // Schedule shadow recalculation after the DOM updates with new rows
        setTimeout(() => { this._updateShadows(); this._cdr.markForCheck(); }, 0);
    }

    /** Returns true when at least one column filter is active */
    hasFilter(): boolean {
        return Object.keys(this._filters).length > 0;
    }

    /** Stub for compatibility with consumers that relied on p-table API */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    reset(): void {}

    /** Clears all active column filters */
    clear(): void {
        this.clearAllFilters();
    }

    private resolveTemplates(): void {
        this.templates.forEach(item => {
            switch (item.name) {
                case 'caption':
                    this.captionTemplate = item.template;
                    break;
                case 'header':
                    this.headerTemplate = item.template;
                    break;
                case 'body':
                    this.bodyTemplate = item.template;
                    break;
                case 'summary':
                    this.summaryTemplate = item.template;
                    break;
                case 'loadingbody':
                    this.loadingBodyTemplate = item.template;
                    break;
                case 'emptymessage':
                    this.emptyMessageTemplate = item.template;
                    break;
            }
        });
    }
}

/**
 * Applies a set of column filters to a dataset using the same matching rules as
 * {@link QwykTableComponent}'s internal filter. Exposed so a parent that owns
 * pagination can filter the full dataset before slicing it into pages (keeping
 * filters, totals and page counts consistent across the whole result set).
 */
export function applyColumnFilters<T>(
    data: T[],
    filters: Record<string, QwykFilterRule>
): T[] {
    if (!data || !filters || Object.keys(filters).length === 0) {
        return data ?? [];
    }
    return data.filter(row =>
        Object.entries(filters).every(([field, rule]) =>
            _matchFilter(_deepGet(row, field), rule.value, rule.matchMode)
        )
    );
}

function _deepGet(obj: unknown, path: string): unknown {
    return path.split('.').reduce((acc, key) => (acc != null ? (acc as Record<string, unknown>)[key] : null), obj);
}

function _matchFilter(value: unknown, filterValue: unknown, matchMode: string): boolean {
    // null/undefined filter = pass-through (shouldn't happen since setFilter guards against it)
    if (filterValue === null || filterValue === undefined || filterValue === '') return true;

    const strVal = String(value ?? '').toLowerCase();
    const strFilter = String(filterValue ?? '').toLowerCase();

    switch (matchMode) {
        case 'contains':         return strVal.includes(strFilter);
        case 'notContains':      return !strVal.includes(strFilter);
        case 'startsWith':       return strVal.startsWith(strFilter);
        case 'endsWith':         return strVal.endsWith(strFilter);
        case 'equals':           return strVal === strFilter;
        case 'notEquals':        return strVal !== strFilter;

        case 'lt':               return Number(value) < Number(filterValue);
        case 'lte':
        case 'formatted_value_less_than_or_equal_2decimals':
        case 'formatted_value_less_than_or_equal_3decimals':
                                 return Number(value) <= Number(filterValue);
        case 'gt':               return Number(value) > Number(filterValue);
        case 'gte':
        case 'formatted_value_greater_than_or_equal_2decimals':
        case 'formatted_value_greater_than_or_equal_3decimals':
                                 return Number(value) >= Number(filterValue);
        case 'formatted_value_equals_2decimals':
        case 'formatted_value_equals_3decimals':
                                 return Number(value) === Number(filterValue);
        case 'formatted_value_not_equals_2decimals':
        case 'formatted_value_not_equals_3decimals':
                                 return Number(value) !== Number(filterValue);

        case 'custom_date_is':   return _sameDay(value, filterValue);
        case 'custom_date_is_not': return !_sameDay(value, filterValue);
        case 'custom_date_before': {
            if (!value) return false;
            // Use _toLocalDate for the row value too, so date-only strings are compared
            // against the same local-midnight reference as the filter value (see _sameDay).
            return _toLocalDate(value).getTime() < _startOfDay(filterValue as string).getTime();
        }
        case 'custom_date_after': {
            if (!value) return false;
            return _toLocalDate(value).getTime() > _endOfDay(filterValue as string).getTime();
        }

        case 'custom_tags': {
            if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
            if (!Array.isArray(value) || value.length === 0) return false;
            return (filterValue as string[]).some(f =>
                (value as { id: string }[]).find(v => v.id === f)
            );
        }

        default: return true;
    }
}

/**
 * Parses a date value as a **local** Date. Date-only ISO strings ("YYYY-MM-DD")
 * are deliberately parsed as local midnight instead of UTC midnight so that date
 * comparisons match the day the user sees in their timezone. Full datetime strings
 * (with a time component) are passed to `new Date()` as usual.
 */
function _toLocalDate(value: unknown): Date {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    return value instanceof Date ? value : new Date(value as string);
}

function _sameDay(a: unknown, b: unknown): boolean {
    if (!a || !b) return false;
    const da = _toLocalDate(a);
    const db = _toLocalDate(b);
    return da.getFullYear() === db.getFullYear() &&
           da.getMonth() === db.getMonth() &&
           da.getDate() === db.getDate();
}

function _startOfDay(d: string): Date {
    const dt = _toLocalDate(d);
    dt.setHours(0, 0, 0, 0);
    return dt;
}

function _endOfDay(d: string): Date {
    const dt = _toLocalDate(d);
    dt.setHours(23, 59, 59, 999);
    return dt;
}
